/**
 * @class
 * Class to create a responsive navigation.
 *
 * It will copy the navigation in the dropdown menu. It prefers a list (ol, ul) as child of the root-node.
 */
class ResponsiveListNavigation {
    /**
     * Creates a responsive navigation, where items that don't fit on the screen get stored in a dropdown menu.
     * @param {Object} options                 - Stores the values of the ResponsiveListNavigation.
     * @property {string} options.rootEl       - The root element on which this class gets called. Default is a class, '.js-tabs-container".
     * @property {string} options.type         - The type of navigation to be displayed. Default is a tab-navigation. It should match the type of list you are tyring to make responsive.
     * @property {string} options.buttonText   - The label for the button. Default is "Meer".
     * @property {Boolean} options.showIcon    - If there should be a icon displayed in the dropdown menu. Default is true
     */
    constructor(options = {}) {
      this.defaultSettings = {
        rootEl: ".js-tabs-container",
        type: "tab",
        get buttonText() {
          return (
            document.querySelector(this.rootEl).dataset.buttonLabel || "Meer"
          );
        },
        showIcon: true,
      };

      this.settings = {
        ...this.defaultSettings,
        ...options,
      };
    }

    /**
     * @init
     * Initialises the responsive menu
     */
    init() {
      const container = document.querySelector(this.settings.rootEl);

      let tabs;
      if (container.children.length === 1) {
        [tabs] = container.children;
      } else if (container.children.length > 1) {
        tabs = [...container.children].find(
          item => item.localName === "ul" || item.localName === "ol",
        );
      } else {
        return;
      }

      const primaryTabs = tabs.querySelectorAll("li:not(.has-more)");
      if (primaryTabs.length === 0) {
        return;
      }

      //  Add a JavaScript availability check. If the user has disabled JavaScript, there is a gracefull
      //  fallback where the user can scroll inside the tabs.
      container.classList.add("js-enabled");

      tabs.insertAdjacentHTML(
        "beforeend",
        `
              <li class="${this.settings.type} has-more">
                  <button class="btn-tab" type="button" aria-haspopup="true" aria-expanded="false">
                      ${this.settings.buttonText} ${
          this.settings.showIcon ? "&darr;" : "&nbsp;"
        }
                  </button>
                  <${tabs.localName} class="menu-item-list">
                      ${tabs.innerHTML}
                  </${tabs.localName}>
              </li>
          `,
      );

      const secondary = container.querySelector(".menu-item-list");
      const secondaryItems = secondary.querySelectorAll("li");
      const allItems = container.querySelectorAll("li");
      const moreLi = tabs.querySelector(".has-more");
      const moreBtn = moreLi.querySelector("button");

      // Show all elements that don't fit on the navigation
      moreBtn.addEventListener("click", e => {
        e.preventDefault();
        container.classList.toggle("show-secondary");
        moreBtn.setAttribute(
          "aria-expanded",
          container.classList.contains("show-secondary"),
        );
      });


      const doAdapt = () => {
        allItems.forEach(item => {
            item.classList.remove("is-hidden");
        });

        let stopWidth = moreBtn.offsetWidth;
        const hiddenItems = [];
        const primaryWidth = tabs.offsetWidth;

        primaryTabs.forEach((item, i) => {
          if (primaryWidth >= stopWidth + item.offsetWidth) {
            stopWidth += item.offsetWidth;
          } else {
            item.classList.add("is-hidden");
            hiddenItems.push(i);
          }
        });

        if (!hiddenItems.length) {
          moreLi.classList.add("is-hidden");
          container.classList.remove("show-secondary");
          moreBtn.setAttribute("aria-expanded", false);
        } else {
          secondaryItems.forEach((item, i) => {
            if (!hiddenItems.includes(i)) {
              item.classList.add("is-hidden");
            }
          });
        }
      };

      doAdapt();
      window.addEventListener("load", doAdapt);
      window.addEventListener("resize", doAdapt);

      // If the user doesn't click on the screen or presses escape, the menu will close, basic UX
      document.addEventListener("click", e => {
        let el = e.target;

        while (el) {
          if (el === secondary || el === moreBtn) {
            return;
          }
          el = el.parentNode;
        }
        container.classList.remove("show-secondary");
        moreBtn.setAttribute("aria-expanded", false);
      });

      document.addEventListener("keyup", e => {
        if (e.key === "Escape") {
          container.classList.remove("show-secondary");
          moreBtn.setAttribute("aria-expanded", false);
        }
      });
    }
  }

  export default ResponsiveListNavigation;
